import React, { useState, useRef, useCallback } from 'react';

import useShowToggle from 'js/social/shared/useShowToggle';

import ConfirmationModal from './ConfirmationModal';

/**
 * This handles the state and actions of the `ConfirmationModal`
 * Component.
 * It returns a ready to use `ConfirmationModal` component with the
 * `showModal` state, along with the set of actions to display the modal
 * and dynamically change the modal contents.
 */
const useConfirmationModal = (): {
  modal: React.ReactElement;
  showModal: boolean;
  openModal: (
    heading: string | undefined,
    content: React.ReactNode,
    action: string,
    onAction: () => void,
  ) => void;
  closeModal: () => void;
  setDisableAction: (disabled: boolean) => void;
} => {
  const [heading, setHeading] = useState<string | undefined>('Confirm Action');
  const [content, setContent] = useState<React.ReactNode>('Are you sure?');
  const [action, setAction] = useState('Submit');
  const [disableAction, setDisableAction] = useState(false);
  const onActionRef = useRef<() => void>(() => {});
  const { isShown, hide, show } = useShowToggle();

  const openModal = useCallback(
    (
      heading: string | undefined,
      content: React.ReactNode,
      action: string,
      onAction: () => void,
    ) => {
      setHeading(heading);
      setContent(content);
      setAction(action);
      onActionRef.current = onAction;
      show();
    },
    [show],
  );

  return {
    modal: (
      <ConfirmationModal
        heading={heading}
        content={content}
        action={action}
        disableAction={disableAction}
        onClose={hide}
        onAction={async () => await onActionRef.current()}
      />
    ),
    showModal: isShown,
    openModal,
    closeModal: hide,
    setDisableAction,
  };
};

export default useConfirmationModal;
